import { Button } from '@/components/ui/button';
import Icon from '@/icons/icon';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

export interface HeroSlideData {
    id: string;
    title: string;
    subtitle: string | null;
    alt: string;
    fine_print: string | null;
    image_url: string | null;
    focal_x: number;
    focal_y: number;
    cta: { label: string } | null;
}

interface HeroSliderProps {
    slides: HeroSlideData[];
}

const AUTOPLAY_MS = 5000;
const CONTAINER_CLASS =
    '@container relative h-[285px] w-full overflow-hidden @[768px]:h-[270px] @[1024px]:h-[350px]';

const SlideContent = memo(function SlideContent({
    slide,
    headingLevel,
}: {
    slide: HeroSlideData;
    headingLevel: 'h1' | 'h2';
}) {
    const Heading = headingLevel;

    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-white @[768px]:px-16 @[1024px]:px-32">
            <Heading className="text-3xl font-bold drop-shadow-lg @[640px]:text-4xl @[1024px]:text-5xl">
                {slide.title}
            </Heading>
            {slide.subtitle && (
                <p className="mt-3 text-lg opacity-90 drop-shadow @[640px]:text-xl @[1024px]:text-2xl">
                    {slide.subtitle}
                </p>
            )}
            {slide.cta && (
                <span className="mt-6 rounded-lg bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-3 font-semibold text-white shadow-lg @[640px]:px-8 @[640px]:py-4">
                    {slide.cta.label}
                </span>
            )}
            {slide.fine_print && (
                <p className="mt-2 text-xs opacity-80 drop-shadow @[640px]:text-sm">
                    {slide.fine_print}
                </p>
            )}
        </div>
    );
});

const SlideImage = memo(function SlideImage({
    slide,
    index,
}: {
    slide: HeroSlideData;
    index: number;
}) {
    return (
        <div className="relative h-full w-full">
            {slide.image_url && (
                <img
                    src={slide.image_url}
                    alt={slide.alt}
                    className="h-full w-full object-cover"
                    style={{
                        objectPosition: `${slide.focal_x}% ${slide.focal_y}%`,
                    }}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent" />
        </div>
    );
});

export default function HeroSlider({ slides }: HeroSliderProps) {
    const [current, setCurrent] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);

    const count = slides.length;

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReducedMotion(query.matches);

        const handler = (event: MediaQueryListEvent) =>
            setReducedMotion(event.matches);
        query.addEventListener('change', handler);

        return () => query.removeEventListener('change', handler);
    }, []);

    const goTo = useCallback(
        (index: number) => {
            if (count === 0) return;
            setCurrent(((index % count) + count) % count);
        },
        [count],
    );

    const next = useCallback(() => goTo(current + 1), [current, goTo]);
    const prev = useCallback(() => goTo(current - 1), [current, goTo]);

    useEffect(() => {
        if (count <= 1 || isPaused || reducedMotion) return;

        const interval = setInterval(next, AUTOPLAY_MS);

        return () => clearInterval(interval);
    }, [count, isPaused, reducedMotion, next]);

    if (count === 0) {
        return null;
    }

    if (count === 1) {
        const slide = slides[0];

        return (
            <div className={CONTAINER_CLASS}>
                <SlideImage slide={slide} index={0} />
                <SlideContent slide={slide} headingLevel="h1" />
            </div>
        );
    }

    const handleTouchStart = (event: React.TouchEvent) => {
        touchStartX.current = event.touches[0].clientX;
    };

    const handleTouchMove = (event: React.TouchEvent) => {
        touchEndX.current = event.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        const diff = touchStartX.current - touchEndX.current;
        if (diff > 50) next();
        else if (diff < -50) prev();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            prev();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            next();
        }
    };

    return (
        <div
            role="region"
            aria-roledescription="carousel"
            aria-label="Featured destinations"
            className={CONTAINER_CLASS}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            onKeyDown={handleKeyDown}
        >
            {slides.map((slide, index) => (
                <div
                    key={slide.id}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${index + 1} of ${count}`}
                    aria-hidden={index !== current}
                    aria-live={
                        index === current && !isPaused && !reducedMotion
                            ? 'off'
                            : 'polite'
                    }
                    className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                        index === current ? 'z-10 opacity-100' : 'z-0 opacity-0'
                    }`}
                >
                    <SlideImage slide={slide} index={index} />
                    <SlideContent
                        slide={slide}
                        headingLevel={index === 0 ? 'h1' : 'h2'}
                    />
                </div>
            ))}

            {/* Arrows */}
            <Button
                variant="bare"
                type="button"
                onClick={prev}
                aria-label="Previous slide"
                className="absolute top-1/2 left-2 z-20 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/80 text-gray-800 shadow hover:bg-card @[640px]:flex"
            >
                <Icon iconName="ChevronLeft" className="h-5 w-5" />
            </Button>
            <Button
                variant="bare"
                type="button"
                onClick={next}
                aria-label="Next slide"
                className="absolute top-1/2 right-2 z-20 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/80 text-gray-800 shadow hover:bg-card @[640px]:flex"
            >
                <Icon iconName="ChevronRight" className="h-5 w-5" />
            </Button>

            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-2">
                {slides.map((slide, index) => (
                    <Button
                        variant="bare"
                        key={slide.id}
                        type="button"
                        onClick={() => goTo(index)}
                        aria-label={`Go to slide ${index + 1}`}
                        aria-current={index === current}
                        className={`h-2 w-2 cursor-pointer rounded-full transition-all ${
                            index === current ? 'w-5 bg-card' : 'bg-white/50'
                        }`}
                    />
                ))}
            </div>
        </div>
    );
}
